import type { MutableRefObject } from "react";
import { useEffect, useMemo, useRef } from "react";
import { getTimeMs } from "@rosette/ts/src/util/utils.js";
import { clamp } from "@rosette/ts/src/util/numbers.js";
import { cn } from "@rosette/ts/src/util/cn.js";


export const SPINNER_DURATION_MS = 4000;


class TimingSpinnerRenderer {
    private endTimeMs: number = 0;
    private colour: string = "blue";
    private pieElemRef: MutableRefObject<HTMLDivElement | null> | null = null;
    private leftElemRef: MutableRefObject<HTMLDivElement | null> | null = null;
    private rightElemRef: MutableRefObject<HTMLDivElement | null> | null = null;

    update(
        endTimeMs: number,
        colour: string,
        pieElemRef: MutableRefObject<HTMLDivElement | null>,
        leftElemRef: MutableRefObject<HTMLDivElement | null>,
        rightElemRef: MutableRefObject<HTMLDivElement | null>,
    ) {
        // Convert to getTimeMs from Date.now, as getTimeMs uses a more accurate timer.
        this.endTimeMs = endTimeMs + getTimeMs() - Date.now();
        this.colour = colour;
        this.pieElemRef = pieElemRef;
        this.leftElemRef = leftElemRef;
        this.rightElemRef = rightElemRef;
    }

    render() {
        const pieElem = this.pieElemRef?.current;
        const leftElem = this.leftElemRef?.current;
        const rightElem = this.rightElemRef?.current;
        if (!pieElem || !leftElem || !rightElem)
            return;

        const remaining = this.endTimeMs - getTimeMs();
        const progress = clamp(1 - remaining / SPINNER_DURATION_MS, 0, 1);
        const degrees = progress * 360;

        const colourPeriod = 500;
        let colourRawValue = remaining % colourPeriod;
        if (colourRawValue < 0) {
            colourRawValue += colourPeriod;
        }
        const colourValue = Math.sin(2 * Math.PI * colourRawValue / colourPeriod);

        let colour;
        if (this.colour === "blue") {
            const saturation = 60 + 18 * progress;
            const lightness = 50 + colourValue * clamp(2 * progress - 1, 0, 1) * 10;
            colour = `hsl(221deg, ${saturation}%, ${lightness}%)`;

        } else if (this.colour === "red") {
            const saturation = 70 + 18 * progress;
            const lightness = 50 + colourValue * clamp(2 * progress - 1, 0, 1) * 10;
            colour = `hsl(15deg, ${saturation}%, ${lightness}%)`;
        } else {
            throw new Error(`Unknown colour ${this.colour}`);
        }

        leftElem.style.transform = `rotate(${degrees}deg)`;
        leftElem.style.borderColor = colour;
        rightElem.style.borderColor = colour;

        if (progress <= 0) {
            pieElem.style.clipPath = "none";
            leftElem.style.display = "none";
            leftElem.style.clipPath = "";
            rightElem.style.display = "none";

        } else if (progress >= 1) {
            pieElem.style.clipPath = "none";
            leftElem.style.display = "";
            leftElem.style.clipPath = "none";
            rightElem.style.display = "none";

        } else if (progress > 0.5) {
            pieElem.style.clipPath = "none";
            leftElem.style.display = "";
            leftElem.style.clipPath = "";
            rightElem.style.display = "";

        } else {
            pieElem.style.clipPath = "";
            leftElem.style.display = "";
            leftElem.style.clipPath = "";
            rightElem.style.display = "none";
        }
    }
}


interface TimingSpinnerHalfProps {
    className?: string;
    elemRef: MutableRefObject<HTMLDivElement | null>;
}


function TimingSpinnerHalf({ className, elemRef }: TimingSpinnerHalfProps) {
    return (
        <div
            className={cn(
                "absolute left-0 top-0 size-full rounded-full border-solid border-[length:calc(2em/3)] border-ruleset-blitz [clip-path:inset(0_50%_0_0)]",
                className ?? null,
            )}
            ref={elemRef} />
    );
}


interface TimingSpinnerProps {
    className?: string;
    endTimeMs: number;
    colour: "red" | "blue";
}


export function TimingSpinner({ className, endTimeMs, colour }: TimingSpinnerProps) {
    const renderer = useMemo(() => new TimingSpinnerRenderer(), []);
    const pieElemRef = useRef<HTMLDivElement | null>(null);
    const leftElemRef = useRef<HTMLDivElement | null>(null);
    const rightElemRef = useRef<HTMLDivElement | null>(null);
    renderer.update(endTimeMs, colour, pieElemRef, leftElemRef, rightElemRef);

    useEffect(() => {
        let running = true;
        let handle: number | null = null;

        const renderLoop = () => {
            handle = null;
            if (!running)
                return;

            renderer.render();
            handle = requestAnimationFrame(renderLoop);
        };

        renderLoop();
        return () => {
            running = false;
            if (handle !== null) {
                cancelAnimationFrame(handle);
            }
        };
    }, [renderer]);

    return (
        <div className={cn(
            "relative size-[2em]",
            className ?? null,
        )}>
            <div className="absolute left-[5%] top-[5%] size-[90%] rounded-full border-solid border-[0.5em] border-[rgb(100_100_100/0.5)]" />
            <div
                className="absolute left-0 top-0 size-full [clip-path:inset(0_0_0_50%)]"
                ref={pieElemRef}>
                <TimingSpinnerHalf elemRef={leftElemRef} />
                <TimingSpinnerHalf className="rotate-180" elemRef={rightElemRef} />
            </div>
        </div>
    );
}
